import { toWebpImageUrl } from '../../utility/image-utility';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, Input, NgZone, OnInit, PLATFORM_ID, Renderer2, ViewChild } from '@angular/core';
import { CountryInfo, IDBProduct, OnlineProduct, ProductOption } from '../../interfaces/product';
import { FormControl } from '@angular/forms';
import { CartService } from '../../services/cart.service';
import { WishlistService } from '../../services/wishlist.service';
import { CompareService } from '../../services/compare.service';
import { RootService } from '../../services/root.service';
import { QuickviewService } from '../../services/quickview.service';
import { UserService } from '../../services/user.service';
import { NijederService } from '../../api/nijeder.service';
import { map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ShopService } from '../../api/shop.service';
import { Router } from '@angular/router'; 
import { Banner } from '../../interfaces/ad-banner'; 
import { CurrencyService } from '../../services/currency.service';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { CurrencyFormatOptions } from '../../interfaces/currency-format-options';
import { CartItem } from '../../interfaces/cart-item';
import { StoreService } from '../../services/store.service';
import { PromotionService, WelcomeDeal } from '../../api/promotion.service';
import { AnalyticsService } from '../../services/analytics.service';
import { CountryShipping, DeliveryOption, EligibilityService, ProductOfferRow, ProductShippingMap } from '../../api/eligibility.service';
import { CountryGateService } from '../../services/country-gate.service';
import { BRAND } from 'src/app/shared/brand';
import { ShopFrontService } from '../../api/shop-front.service';
import { shopStatLines } from '../shop-card/shop-stats';
import { CultureService } from '../../culture/culture.service';
import { askConfirm, askPrompt, showAlert } from 'src/app/shared/dialogs/app-dialog';

export type ProductLayout = 'standard' | 'sidebar' | 'columnar' | 'quickview';

interface Item {
    cartItem: CartItem;
    quantity: number;
    action:string;
}

@Component({
    selector: 'app-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.scss']
})
export class ProductComponent implements OnInit, AfterViewInit
{
    /// The brand, for the template (shared/brand.ts).
    readonly brand = BRAND;

    @HostListener('unloaded')
    ngOnDestroy() {
        if (this.sizeRowsRO) { this.sizeRowsRO.disconnect(); this.sizeRowsRO = null; }
    }

    @Input() layout: ProductLayout; 
    @Input() product: IDBProduct;
    @Input() isQuickView:boolean;

    /// The related products the page loaded (PageProductComponent, carried from the server render), for the sold-out
    /// box's "in stock now" cards. Only the product page passes them; the quick view has none.
    @Input() set related(list: OnlineProduct[])
    {
        const self = this.product;
        // The server sends only what can be bought and what sells in the visitor's country; the checks here are for a
        // list cached before that, and for the product itself.
        this.alternatives = (list || [])
            .filter(p => p && p.AvailableQuantity > 0 && !(self && (p.PUID === self.PUID || p.Slug === self.Slug)))
            .slice(0, ProductComponent.alternativesShown);
    }

    /// What the sold-out box offers instead (the related products in stock), four at most.
    alternatives: OnlineProduct[] = [];
    static readonly alternativesShown = 4;

    adBanner:Banner = {adClient:'ca-pub-7547733261487112', adSlot:1179812238, adFormat:'auto', fullWidthResponsive:true};

    quantity: FormControl = new FormControl(1);
    private destroy$: Subject<void> = new Subject();
    addingToCart = false;
    addingToWishlist = false;
    addingToCompare = false;
    gettingUrl:boolean;
    deleting:boolean;

    /// The owner's panel: inventory and the tracked listing behind this product. Loaded in
    /// the browser once the signed-in user turns out to be the owner; never in SSR.
    ownerInfo:any = null;
    ownerInfoLoading = false;
    ownerInfoError:string = null;

    /// The owner can hide the whole owner view to see the page as a shopper does. Kept in this
    /// browser, so it stays hidden from product to product until shown again.
    ownerPanelOpen = true;
    private static readonly OwnerPanelKey = 'izz_owner_panel';
    isMyProduct:boolean;
    userInfo:any = {};  
    selectedOption:any;
    selectedPriceOption:ProductOption;
    selectedImages:any[];
    selectedMax:number;
    highlightFeatures:any = {item1:{name:"", icon:""}, item2:{name:"", icon:""}, item3:{name:"", icon:""}, item4:{name:"", icon:""}}
    show = false;
    sellAt = 45;

    /// The first-order welcome deal, when the viewer qualifies (PromotionService).
    welcomeDeal: WelcomeDeal = null;
    dStartDate:any;
    dEndDate:any;
    isMobile = true
    currencyOptions:CurrencyFormatOptions;
    items: Item[] = [];
    countryInf:CountryInfo;

    /// The carrier the supplier would actually ship this variant with, to this shopper's
    /// country. Null until it answers, and null forever when the shop is not connected to the
    /// API - the page then shows the stored estimate, exactly as it always did.
    shipping:DeliveryOption = null;

    /// What the shop knows about delivering this product to the visitor's own country, and
    /// nothing about any other: the country layer works behind the page, it is not shown on
    /// it. Null until the country is known, and null when nobody has checked it yet - the box
    /// then shows the product's own estimate, as it always did.
    myShipping:CountryShipping = null;

    /// The product's stored delivery facts, country by country. Never rendered: it goes into
    /// the structured data for search engines, and the visitor's own row is taken out of it.
    private shipMap:ProductShippingMap = null;

    /// Owner only: every country with its fact, verdict and findings, and the overrides.
    matrix:any = null;
    matrixOpen = false;
    matrixLoading = false;

    /// The independent seller of this product (product.Seller: ShopPages, with the shop page's facts), for the "Sold by"
    /// block; null for the shop's own products (SID 786), which keep their own line.
    get seller(): any
    {
        return this.product && this.product.SID !== 786 ? (this.product as any).Seller || null : null;
    }

    /// What the block says of the seller: "4.7 out of 5 (31)", "96% delivered" - each only when there is one. Worked out
    /// once per seller, not on every change detection.
    get sellerFacts(): string[]
    {
        const seller = this.seller;
        if (seller !== this.sellerFactsOf) {
            this.sellerFactsOf = seller;
            this.sellerFactLines = seller
                ? shopStatLines({ RatingAvg: seller.RatingAvg, RatingCount: seller.RatingCount, DeliveredRate: seller.DeliveredRate }, this.labels)
                    .map(line => line.text)
                : [];
        }
        return this.sellerFactLines;
    }
    private sellerFactsOf: any = null;
    private sellerFactLines: string[] = [];

    /// The signed-in shopper follows the seller (asked in the browser only); a host with following off answers 404 and
    /// the button goes.
    sellerFollowing = false;
    sellerFollowable = true;
    sellerFollowBusy = false;
    readonly isBrowser: boolean;

    /// Owner: the product's history (ProductSource/History) - every change and every check.
    history:any[] = null;
    historyRows:any[] = [];
    historyOpen = false;
    historyLoading = false;
    historyMore = false;
    historyShown:number = null;

    @ViewChild('#ad') script: ElementRef;

    @ViewChild('sizeList') sizeListRef: ElementRef<HTMLElement>;

    /// The Size row shows at most 2 rows of chips, on every screen - a phone's narrower row
    /// often fits fewer chips than the desktop sidebar, so it reaches this cap more often, not
    /// less. Rows are read from the rendered chips' own offsetTop (measureSizeRows) rather than
    /// counted, because flex-wrap's row count depends on the container's width.
    sizesExpanded = false;
    sizesOverflow = false;
    sizeCollapsedHeight = 0;
    sizeFadeTop = 0;
    private sizeRowsRO: any = null;
    private sizeMeasurePending = false;

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private zone: NgZone,
        private cart: CartService,
        private cd: ChangeDetectorRef,
        private wishlist: WishlistService,
        private compare: CompareService,
        public quickview: QuickviewService,
        public root: RootService,
        private user:UserService,
        private toastr:ToastrService, 
        private router:Router,
        private shop:ShopService,
        public currency:CurrencyService,
        private culture:StoreService,
        private promotion:PromotionService,
        private analytics:AnalyticsService,
        private eligibility:EligibilityService,
        private gate:CountryGateService,
        private nijer:NijederService,
        private state:TransferState,
        private shopFront:ShopFrontService,
        private labels:CultureService
    )
    {
        this.isBrowser = isPlatformBrowser(platformId);
        this.toastr.toastrConfig.preventDuplicates = true
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;

        this.cart.onLimit$.pipe(takeUntil(this.destroy$)).subscribe(res=>
        {
            if(res)
            { 
                if(res && res.MaxExceed == true)
                {
                    this.toastr.warning(this.labels.t('max-quantity-limit', 'Maximum quantity limit is {limit}', { limit: res.MaxLimit }));
                }
                else if(res && res.ID > 0)
                {
                    this.selectedPriceOption.Quantity -= this.quantity.value; 
                }
            }
        });   
        this.user.onTabChanged$.next("specification");
    }
    
    ngAfterViewInit(): void {
        if (isPlatformBrowser(this.platformId)) {
          // Wait for the view to be initialized before scrolling
        //   setTimeout(() => {
        //     this.scrollToTarget();
        //   }, 0); // small delay to ensure everything is rendered
            this.scheduleSizeMeasure();
            this.observeSizeList();
        }
      }

    /// Re-reads the Size row's rows on the next tick - after a variant switch re-renders the
    /// chips, or the container's own box has just resized (observeSizeList). Debounced across a
    /// burst of either.
    private scheduleSizeMeasure(): void
    {
        if (!isPlatformBrowser(this.platformId) || this.sizeMeasurePending) { return; }
        this.sizeMeasurePending = true;
        setTimeout(() => {
            this.sizeMeasurePending = false;
            this.measureSizeRows();
        });
    }

    /// Width changes the row count under flex-wrap (a window resize, an orientation change, the
    /// sidebar reflowing at a breakpoint) - so the cap is re-measured from the container's own
    /// box, not just once at load. ResizeObserver runs outside Angular's zone, hence zone.run.
    private observeSizeList(): void
    {
        const container = this.sizeListRef && this.sizeListRef.nativeElement;
        if (!container || typeof (window as any).ResizeObserver === 'undefined') { return; }
        this.sizeRowsRO = new (window as any).ResizeObserver(() => this.zone.run(() => this.scheduleSizeMeasure()));
        this.sizeRowsRO.observe(container);
    }

    /// More than 2 rows of Size chips: cap it at 2 (sizesOverflow), whatever the screen. Rows are
    /// read from the chips' own offsetTop rather than counted off the data, because flex-wrap's
    /// row count depends on the container's width, which differs by device and by window size.
    private measureSizeRows(): void
    {
        if (!isPlatformBrowser(this.platformId)) { return; }
        const container = this.sizeListRef && this.sizeListRef.nativeElement;
        if (!container) { this.sizesOverflow = false; return; }

        const rowTops: number[] = [];
        for (let i = 0; i < container.children.length; i++)
        {
            const el = container.children[i] as HTMLElement;
            if (el.tagName !== 'LABEL') { continue; }
            const top = el.offsetTop;
            if (!rowTops.some(t => Math.abs(t - top) < 4)) { rowTops.push(top); }
        }
        rowTops.sort((a, b) => a - b);

        this.sizesOverflow = rowTops.length > 2;
        if (this.sizesOverflow)
        {
            // Row 2's own top (the fade starts there) and row 3's top (where the collapsed
            // height ends, clipping row 3 and beyond entirely).
            this.sizeFadeTop = rowTops[1];
            this.sizeCollapsedHeight = rowTops[2];
        }
        else
        {
            this.sizesExpanded = false;
        }
        this.cd.markForCheck();
    }

    toggleSizes(): void
    {
        this.sizesExpanded = !this.sizesExpanded;
    }
    
      scrollToTarget(): void {
        if (isPlatformBrowser(this.platformId)){
            const targetElement = document.getElementById('showDiv');
            if (targetElement) {
              targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start'  // Scroll to the top of the element
              });
            }
        } 
      }

    ngOnInit(): void 
    {   
        // Not the guess made before Locale/me answers (Pending): until the visitor's own country is in, the page stays
        // as the server drew it - no country named, nothing quoted or decided for the United States on its account.
        this.culture.onCountry.pipe(takeUntil(this.destroy$)).subscribe(res =>{
            if(res && !res.Pending){
                this.countryInf = res;
                this.pickMyShipping();
                this.loadMyOffer();
                this.leaveIfUnavailable();
                this.cd.markForCheck();
            }
        })
        this.currencyOptions = this.currency.options;

        // Fetched in the browser only, so the server render and crawlers get the plain price -
        // the one the structured data states.
        this.promotion.welcomeDeal$.pipe(takeUntil(this.destroy$)).subscribe(deal => {
            this.welcomeDeal = deal;
            this.cd.markForCheck();
        });

        this.currency.changes$.pipe(takeUntil(this.destroy$)).subscribe(res =>{ 
            if(res){
                this.currencyOptions = res;
            }
        })
        this.user.onUserInfoChanged$.pipe(takeUntil(this.destroy$)).subscribe(res =>
        { 
            this.isMyProduct = false;
            
            this.userInfo = res; 
            if(this.userInfo !== null && this.userInfo.SID === this.product.SID)
            {
                this.isMyProduct = true;
            }
            if (this.isOwner())
            {
                // Nothing is fetched for a view the owner has hidden.
                this.ownerPanelOpen = this.ownerPanelPreference();
                if (this.ownerPanelOpen) { this.loadOwnerInfo(); }
            }
            this.cd.markForCheck();
        }); 

        this.showSeller();
        this.initProductOptions();
        this.shop.setRecentView(this.product);
        // The product's own estimate until its delivery table arrives; only a product with no
        // table (added by hand, or not checked yet) keeps showing it. The supplier's live quote
        // is asked for after the table, and only where it has nothing for the visitor.
        const [startDate, endDate] = this.calculateDeliveryRange(this.product.MaxDeliveryDays);
        this.dStartDate = startDate
        this.dEndDate = endDate;
        this.loadShipMap();

        // A first visit carries no proof of a person yet, so neither the server nor the resolver
        // sent this visitor anywhere - which is exactly what keeps every crawler on this page.
        // The proof arrives with their first real input event; then they are taken to the
        // "not available" page like anyone else. It fires at once for a returning visitor.
        this.gate.human$.pipe(takeUntil(this.destroy$)).subscribe(() => this.leaveIfUnavailable());
        // A view is a person's, in their browser: the server render does not count (Product/pvcount, ProductViews).
        if (isPlatformBrowser(this.platformId)) {
            this.shop.getpvcount(this.product.Slug).pipe(takeUntil(this.destroy$)).subscribe(() => { }, () => { });
        }

        this.cart.items$.pipe(
            takeUntil(this.destroy$),
            map(cartItems =>
                cartItems
                .filter(cartItem => cartItem.product.ID === this.product.ID)
                .map(cartItem => ({
                    cartItem,
                    quantity: cartItem.quantity,
                    action: 'any'
                }))
            )
        ).subscribe(items => {
            this.items = items;
            if(this.items && this.items.length > 0){
                this.quantity.setValue(items[0].quantity)
            } 
        });
        
        // With the variant initProductOptions just selected, so the price is the one on screen.
        // Adds to cart, wishlist and compare are reported by those services, once the add
        // has actually succeeded.
        this.analytics.viewItem(this.product, this.selectedPriceOption);
    }

    /// Whether the signed-in shopper follows this product's independent seller, for the "Sold by" block's Follow - asked
    /// in the browser only, so the page the server draws is the same for everyone.
    private showSeller(): void
    {
        const seller = this.seller;
        this.sellerFollowing = false;
        this.sellerFollowable = true;

        // The quick view has no room for it, and a shopper signed out is asked to sign in when they press Follow.
        if (!this.isBrowser || !seller || !seller.Slug || !seller.ShopPage || !seller.Follow || this.layout === 'quickview' || !this.user.isLoggedIn()) { return; }
        this.shopFront.followState(seller.Slug).pipe(takeUntil(this.destroy$)).subscribe(state => {
            this.sellerFollowing = !!(state && state.Following);
            this.cd.markForCheck();
        }, error => {
            // Following is off on this site: no button.
            if (error && error.status === 404) { this.sellerFollowable = false; }
            this.cd.markForCheck();
        });
    }

    /// Follow or stop following the seller; a shopper signed out signs in first and comes back to this product.
    toggleSellerFollow(): void
    {
        if (!this.seller || !this.seller.Slug || this.sellerFollowBusy) { return; }
        if (!this.user.isLoggedIn()) {
            this.router.navigate(['/account/login'], { queryParams: { returnUrl: this.router.url } });
            return;
        }
        const want = !this.sellerFollowing;
        this.sellerFollowBusy = true;
        this.shopFront.follow(this.seller.Slug, want).pipe(takeUntil(this.destroy$)).subscribe(res => {
            this.sellerFollowBusy = false;
            if (!res || res.Refused) {
                this.toastr.error((res && res.Refused) || this.labels.t('did-not-work-try-again', 'That did not work. Please try again.'));
            } else {
                const state = res.Value || res;
                this.sellerFollowing = state.Following !== undefined && state.Following !== null ? !!state.Following : want;
            }
            this.cd.markForCheck();
        }, () => {
            this.sellerFollowBusy = false;
            this.toastr.error(this.labels.t('did-not-work-check-connection', 'That did not work. Check your connection and try again.'));
            this.cd.markForCheck();
        });
    }

    /// What the supplier will do for the SKU on screen to the visitor's country - asked for only
    /// where the product's delivery table has nothing for that country (askLiveQuoteIfNeeded).
    /// The SKU on screen is the default warehouse's; where the table knows which warehouse serves
    /// the visitor, a quote about another one would put its dates and carrier beside the right
    /// warehouse's price. It used to be asked on every view and to win.
    ///
    /// Runs in the browser only: server-side rendering would make every crawl of every product
    /// page spend a metered API call, and the markup it produced would then be cached with one
    /// visitor's country baked into it. Quotes are cached server side for twelve hours, so a
    /// busy page costs one call, not one per view.
    loadShippingEstimate():void
    {
        if (!isPlatformBrowser(this.platformId)) { return; }

        if (!this.product || !(this.product.ID > 0)) { return; }

        // By the shop's own ids: the listing and the supplier's SKU ids never reach a browser.
        // A product typed in by hand has nothing to quote, and the answer says so.
        var country = (this.countryInf && this.countryInf.CountryCode) || '';
        var optionId = this.selectedPriceOption ? (this.selectedPriceOption as any).ID : 0;

        this.eligibility.deliveryQuote(this.product.ID, optionId, country).pipe(takeUntil(this.destroy$)).subscribe(
            res =>
            {
                if (!res || res.Ok !== true || !res.Options || res.Options.length === 0) { this.cd.markForCheck(); return; }

                // Tracked first, then cheapest, then fastest - the option the shop would buy.
                this.shipping = res.Options[0];

                if (this.shipping.MaxDeliveryDays > 0)
                {
                    this.setDeliveryDates(this.shipping.MinDeliveryDays, this.shipping.MaxDeliveryDays);
                }

                this.cd.markForCheck();
            },
            () => { this.cd.markForCheck(); /* a missing quote is not worth an error here */ }
        );
    }

    initProductOptions()
    {
        if(this.product.OptionList && this.product.OptionList.length > 0)
        { 
            // The page opens on something that can be bought: the first variant with ANY size in
            // stock, and that size. It used to look only at each variant's first size, so a
            // product whose first size was sold out opened reading "Out of stock" with stock
            // further down the list.
            var inStock = (item:any) => item && item.Quantity > 0 && item.IsActive !== false;

            // Opened from a card that was showing another colour (OpenVariant): that colour's
            // first size in stock, the row the card's price came from (Showcase.Build on the
            // server). This also runs again once the visitor's own offer has loaded, and keeps
            // the colour while it has stock for them; with none, the page opens as it always did.
            var shown = this.product.OpenVariant
                ? this.product.OptionList.find(group => group && group.option === this.product.OpenVariant && (group.items || []).some(inStock))
                : null;
            // With none on the shelf anywhere, a size its seller takes pre-orders or backorders for, before a sold-out one.
            var orderable = (item:any) => this.buyableItem(item);
            this.selectedOption  = shown || this.product.OptionList.find(group => (group.items || []).some(inStock))
                || this.product.OptionList.find(group => (group.items || []).some(orderable)) || this.product.OptionList[0];

            if(this.selectedOption && this.selectedOption.items && this.selectedOption.items.length > 0)
            {
                var opening = this.selectedOption.items.find(inStock) || this.selectedOption.items.find(orderable) || this.selectedOption.items[0];
                this.selectedImages = this.galleryFor(opening.ImageList || this.selectedOption.items[0].ImageList);
                this.selectedPriceOption = opening;
                this.selectedMax = this.maxFor(this.selectedPriceOption);
            }  
        }  
    }

    addToCart(): void
    {
        if (this.soldOut || this.optionSoldOut)
        {
            this.toastr.warning(this.takenOffline ? this.labels.t('product-not-available-now', 'This product isn\'t available right now.')
                : this.soldOut ? this.labels.t('product-is-out-of-stock', 'This product is out of stock.') : this.labels.t('product-option-out-of-stock-choose', 'This option is out of stock. Choose another.'));
            return;
        }

        if (this.notDeliverable)
        {
            this.toastr.warning(this.labels.t('product-not-available-now', 'This product isn\'t available right now.'));
            return;
        }

        if (!this.addingToCart && this.product && this.quantity.value > 0) 
        {
            if(this.selectedPriceOption && this.maxFor(this.selectedPriceOption) < this.quantity.value)
            { 
                this.toastr.warning(this.labels.t('max-quantity-limit', 'Maximum quantity limit is {limit}', { limit: this.selectedMax }));
                return;
            }

            this.addingToCart = true; 

            this.cart.add(this.product, this.quantity.value, this.selectedPriceOption, this.isQuickView)
            .subscribe({
                complete: () => 
                {
                    this.addingToCart = false;
                    // this.cart.load();
                }
            });
        }
    }

    addToWishlist(): void 
    {
        if (this.addingToWishlist) {
            return;
        }

        this.addingToWishlist = true;

        this.wishlist.add(this.product).subscribe({
            complete: () => {
                this.addingToWishlist = false;
                this.cd.markForCheck();
            }
        }); 
    }

    addToCompare(): void 
    {
        if (!this.addingToCompare && this.product) {
            this.addingToCompare = true;

          this.compare.add(this.product).subscribe(
            {complete: () => 
                {
                    this.addingToCompare = false;
                }
            });
        }
    }

    editProduct()
    {
        this.quickview.showAddEditProduct(this.product).subscribe({
            complete: () => {
                //this.showingQuickview = false;
                this.cd.markForCheck();
            }
        });
    }

    /// One variant's gallery: the product's video first, whichever variant is on screen - the one a
    /// card opened, or one picked here - then that variant's pictures. A new list every time: the
    /// video used to be unshifted into the FIRST variant's own list, so no other variant had it, and
    /// the page's second pass (after the visitor's offer loads) put it in twice.
    galleryFor(images: any[]): any[]
    {
        const video = this.product && this.product.Video;
        const pictures = (images || []).filter(image => image && image !== video);
        return video ? [video, ...pictures] : pictures;
    }

    optionChoose(option:any)
    { 
        this.selectedOption = option;
        console.log(this.selectedOption)

        if(this.selectedOption && this.selectedOption.items && this.selectedOption.items.length > 0)
        {
            // The colour's first size that can be bought, as the page opens (initProductOptions). It took the first
            // row, so a colour whose first row was sold out read "Out of stock" with stock further down.
            this.selectedImages = this.galleryFor(this.selectedOption.items[0].ImageList);
            this.selectedPriceOption = this.selectedOption.items.find(item => this.buyableItem(item)) || this.selectedOption.items[0];
            this.selectedMax = this.maxFor(this.selectedPriceOption);
        }
        console.log(this.selectedOption);
        this.sizesExpanded = false;
        this.scheduleSizeMeasure();
        this.scrollToView('mobile-header');
    }

    sizeChoose(option:ProductOption)
    {
        this.selectedPriceOption = option;
        this.selectedMax = this.maxFor(this.selectedPriceOption);
        console.log(this.selectedPriceOption);
    }

    scroll(id: string) 
    {
        if (isPlatformBrowser(this.platformId)){
            this.user.onTabChanged$.next("reviews");
            let el = document.getElementById(id);
            el.scrollIntoView({behavior:"smooth"});
        } 
    } 

    scrollToView(id: string) 
    { 
        if (isPlatformBrowser(this.platformId)){
            let el = document.getElementById(id);
            el.scrollIntoView({behavior:"smooth"});
        } 
    } 

    calculateDeliveryRange(deliveryDate: number): [Date, Date] {
        const today = new Date();
        let startDays: number;
        let endDays: number;
     
        if (deliveryDate <= 4) {
            startDays = deliveryDate;
            endDays = deliveryDate;
        } 
        else if (deliveryDate === 10 || deliveryDate === 9) {
            startDays = 5;
            endDays = 9;
        } else if (deliveryDate <= 15 && deliveryDate >= 11) {
            startDays = 8;
            endDays = 14;
        } else if (deliveryDate === 7) {
            startDays = 4;
            endDays = 7;
        } else if (deliveryDate === 5) {
            startDays = 2;
            endDays = 5;
        } 
        else if (deliveryDate >= 15) {
            startDays = 12;
            endDays = deliveryDate;
        }
        else {
            startDays = 3;
            endDays = 3;
        }
     
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + endDays);

        const startDate = startDays === endDays ? null : new Date(today);
        if (startDate !== null) {
            startDate.setDate(today.getDate() + startDays);
        }

        return [startDate, endDate];
    }

    /// A brand row is only worth showing for a real brand. Imported products can carry the
    /// marketplace's "no brand" placeholders (NONE, NoEnName_Null, OEM), which are not brands
    /// and must not become a link to /products/brand/none.
    hasBrand(): boolean {
        const name = (this.product && this.product.BrandName || '').trim();
        if (!name || !this.product.BrandSlug) { return false; }
        return !/^(none|noenname_null|no brand|no-brand|nobrand|unbranded|oem|others?|n\/a|na|null|generic)$/i.test(name);
    }

    haveSameMonth(date1: Date, date2: Date): boolean {
        // A product with no delivery estimate has no start date (see calculateDeliveryRange),
        // and the template asks this before it checks for that. Answer "same month" so the
        // single-date form renders. Throwing here aborted change detection for the WHOLE page
        // on every pass, which is why the carousels below the product came out empty.
        if (!date1 || !date2) { return true; }
        var res = date1.getMonth() === date2.getMonth(); 
        return res;
    }

    /// Delegates to the shared helper so both product screens agree on which urls have a
    /// webp sibling. See toWebpImageUrl: remote (imported) images must be left alone.
    getWebpImageUrl(imageUrl: string): string {
        return toWebpImageUrl(imageUrl);
    } 

    toTitleCase(input: string): string {
        return input
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    /// What the welcome deal takes off this option bought on its own. The checkout takes it
    /// off the whole first order, capped - for a single item that is this same figure.
    get welcomeSaving(): number {
        return PromotionService.discountFor(this.welcomeDeal, this.selectedPriceOption ? this.selectedPriceOption.Price : 0);
    }

    get welcomePrice(): number {
        const price = this.selectedPriceOption ? this.selectedPriceOption.Price : 0;
        return Math.round((price - this.welcomeSaving) * 100) / 100;
    }

    /// Fewer than this many of the selected variant left shows the low-stock badge.
    static readonly lowStockBelow = 10;

    get isLowStock(): boolean {
        if (this.soldOut || this.optionSoldOut) { return false; }
        const left = this.selectedPriceOption ? this.selectedPriceOption.Quantity : 0;
        return left > 0 && left < ProductComponent.lowStockBelow;
    }

    /// What delivery of one unit costs this visitor, in USD: their country's rate where the
    /// product has one, else the default country's (US); 0 is free. The supplier's fee at cost -
    /// the basket adds it up and the checkout charges it, from the same rates. The server render
    /// does not know the visitor and shows the default, the same for everyone.
    ///
    /// The visitor's offer first, where it says what delivery of the size on screen is: a fee that is
    /// large beside the product's cheapest size is split - part on the price of every size, the rest
    /// here - and the offer is where that is worked out, so this line and the price always agree.
    get shippingFee(): number {
        const option = this.selectedPriceOption;
        const own = option ? this.offerDelivery[option.ID] : undefined;
        if (own !== undefined && own !== null) { return Number(own) || 0; }

        const map: any = this.shipMap;
        if (!map) { return 0; }

        const mine: any = this.myShipping;
        if (mine && mine.State === 'ships' && mine.Fee !== null && mine.Fee !== undefined) { return Number(mine.Fee) || 0; }

        return Number(map.DefaultFee) || 0;
    }

    /// The price row shows a sale: a higher regular price struck through, or a discount badge.
    /// Without one, the row names the free shipping instead.
    get showsSale(): boolean {
        const option = this.selectedPriceOption;
        return !!option && (option.RegularPrice > option.Price || (option.RegularPrice > 0 && option.Discount > 0));
    }

    /// The price in large whole and small cents, converted as every price is (CurrencyService.priceParts).
    getFormattedPrice() {
        return this.currency.priceParts(this.selectedPriceOption.Price);
    }

    getCountryName(){
        return this.countryInf?.Name ?? "Australia"
    }
    
    getSrc(){
        if(isPlatformBrowser(this.platformId)){
            this.gettingUrl = true;
            this.user.getSrc(this.product.Slug).pipe(takeUntil(this.destroy$)).subscribe(res =>
            {
                if(res && res != null)
                {
                    window.open(res, "_blank");
                }
                this.gettingUrl = false;
            });
        } 
    }

    /// The site owner (SID 786), the same rule the account pages use.
    isOwner(): boolean
    {
        return !!this.userInfo && this.userInfo.SID === 786;
    }

    editUrl(): string
    {
        return '/account/productpublish/' + encodeURIComponent(this.product && this.product.Slug || '');
    }

    /// The editor in a new tab, so the storefront view stays open for comparison.
    openEdit()
    {
        if (isPlatformBrowser(this.platformId))
        {
            window.open(this.editUrl(), '_blank', 'noopener');
        }
    }

    /// The product's stored delivery facts: one cached read, on the server render too, because
    /// that is where a crawler reads the structured data. Nothing of it is rendered as a list.
    ///
    /// A render drawn for a returning visitor (StoreService.drawnForVisitor) carries it in the page, and the
    /// browser takes it from there at once: asked again, the box went from the product's own estimate to the
    /// default country's to the visitor's while the page woke up. Never carried for a crawler or a first visit -
    /// their page is what it always was.
    loadShipMap():void
    {
        if (!this.product || !(this.product.ID > 0)) { return; }

        const key = ProductComponent.shipMapKey(this.product.ID);
        if (isPlatformBrowser(this.platformId) && this.state.hasKey(key))
        {
            const carried = this.state.get<ProductShippingMap>(key, null);
            this.state.remove(key);
            this.useShipMap(carried);
            return;
        }

        this.eligibility.productMap(this.product.ID).pipe(takeUntil(this.destroy$)).subscribe(map =>
        {
            if (isPlatformServer(this.platformId) && this.culture.drawnForVisitor)
            {
                this.state.set(key, map || null);
            }
            this.useShipMap(map);
        }, () =>
        {
            // Nothing known is not an error: the product's own estimate stays, and a live quote may do better.
            this.shipMapSettled = true;
            this.askLiveQuoteIfNeeded();
        });
    }

    private useShipMap(map:ProductShippingMap):void
    {
        this.shipMap = map && map.Countries && map.Countries.length > 0 ? map : null;
        this.shipMapSettled = true;

        if (this.shipMap && this.layout !== 'quickview')
        {
            this.root.insertProductSchema(this.product, 'structured-data-product', this.shipMap);
        }

        // The default country's dates first, on the server render too: that is the window the
        // structured data gives the default country, and a crawler must read one promise, not two.
        this.applyDefaultDelivery();
        this.pickMyShipping();
        // The visitor's own answer is in: a product it calls unavailable is left now.
        this.leaveIfUnavailable();
        this.cd.markForCheck();
    }

    /// What a render drawn for the visitor carries to the browser (TransferState): the delivery table, and the
    /// visitor's offer for their country.
    private static shipMapKey(productId:number) { return makeStateKey<ProductShippingMap>('izz-ship-map-' + productId); }
    private static offerKey(productId:number, country:string) { return makeStateKey<any[]>('izz-offer-' + productId + '-' + country); }

    private shipMapSettled = false;
    private liveQuoteFor = '';

    /// The shop cannot say this product reaches the visitor's country: its delivery is checked
    /// country by country and that country is not known to deliver (the map's "unknown"). The
    /// owner's rule (2026-09-23) is that this is treated exactly like a country known not to
    /// deliver - the page says unavailable and the cart is off - because the shop does not offer
    /// what it cannot say it can deliver. The ADMIN is told which of the two it is: this one
    /// means the supplier has offered nothing yet, not that it refused.
    get deliveryNotConfirmed():boolean
    {
        const row = this.myCountryRow();
        return !!row && row.State === 'unknown';
    }

    /// The default country (US - ShippingRates.DefaultCountry) as the page's dates until the
    /// visitor's own row is known, and for good on the server render, which is the same for everyone.
    private applyDefaultDelivery():void
    {
        const row = this.shipMap ? this.shipMap.Countries.find(c => c.Code === 'US') : null;
        if (row && row.State === 'ships' && row.MaxDays > 0)
        {
            this.setDeliveryDates(row.MinDays, row.MaxDays);
        }
    }

    /// The supplier's live quote, once, and only where the delivery table has no row for the
    /// visitor's country - or there is no table at all.
    private askLiveQuoteIfNeeded():void
    {
        // Nothing to deliver: a supplier quote for a product out of stock, or offline, is a call spent for nothing.
        if (!isPlatformBrowser(this.platformId) || !this.shipMapSettled || this.stockedOut || this.takenOffline) { return; }

        const code = ((this.countryInf && this.countryInf.CountryCode) || '').toUpperCase();
        if (!code) { return; }

        // A country known to deliver (with its window) or known not to has its answer. One that is
        // not confirmed is still asked: the supplier may deliver there, and this is the only thing
        // that asks on demand - a country nobody has asked about is unavailable until something does.
        const row = this.myCountryRow();
        if (row && (row.State === 'no' || (row.State === 'ships' && row.MaxDays > 0))) { return; }

        const key = this.product.ID + ':' + code;
        if (this.liveQuoteFor === key) { return; }
        this.liveQuoteFor = key;
        this.loadShippingEstimate();
    }

    /// The shop's own handling time from the delivery table: payment to the order being placed.
    private handlingDays():[number, number]
    {
        const min = Math.max(0, Number(this.shipMap && this.shipMap.HandlingMinDays) || 0);
        const max = Math.max(min, Number(this.shipMap && this.shipMap.HandlingMaxDays) || 0);
        return [min, max];
    }

    /// A promised delivery: today, plus the shop's handling time, plus the supplier's window, in
    /// calendar days like the supplier's - the same figures the structured data gives search
    /// engines. It replaced a table that reshaped the supplier's maximum into rough bands and
    /// ignored the minimum: 5 to 8 days read "3 days", 6 to 10 read 5 to 9 - earlier than the
    /// supplier promises in 683 of 3,148 countries, and the refund policy counts from that window.
    deliveryWindow(minDays:number, maxDays:number):[Date, Date]
    {
        const [handlingMin, handlingMax] = this.handlingDays();
        const max = Math.max(0, Number(maxDays) || 0);
        const min = Math.min(max, Math.max(0, Number(minDays) || 0) || max);

        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() + handlingMin + min);
        const end = new Date(today);
        end.setDate(today.getDate() + handlingMax + max);

        return [start.getTime() === end.getTime() ? null : start, end];
    }

    private setDeliveryDates(minDays:number, maxDays:number):void
    {
        const [from, to] = this.deliveryWindow(minDays, maxDays);
        this.dStartDate = from;
        this.dEndDate = to;
    }

    /// The visitor's own country out of the map, once it is known: in the browser, and on a server
    /// render drawn for a returning visitor (StoreService.drawnForVisitor), which then says what the
    /// browser would - the country named in the box with ITS fee and dates, never with the default's.
    /// A crawler's render never knows a country (countryInf stays unset) and keeps the default.
    private pickMyShipping():void
    {
        // The admin too: they are never redirected away from a product, but the page tells
        // them what it would tell anyone in their country.
        const code = ((this.countryInf && this.countryInf.CountryCode) || '').toUpperCase();
        this.myShipping = code && this.shipMap ? (this.shipMap.Countries.find(c => c.Code === code) || null) : null;
        this.applyCountryDelivery();
        this.askLiveQuoteIfNeeded();
    }

    myCountryRow():CountryShipping
    {
        return this.myShipping;
    }

    private offerFor = '';

    /// What delivery of one unit of each size is charged in the visitor's country, where their offer
    /// says (shippingFee). Empty until it arrives, and for every size it does not speak about.
    private offerDelivery: { [optionId: number]: number } = {};

    /// A product held in several warehouses sells each shopper the SKU of the warehouse that
    /// delivers to them, with its own price and stock, and a product whose delivery fee to them is
    /// a small one - or large beside its price - carries it, or part of it, in the price
    /// (WarehouseOffers). The visitor's own figures replace the
    /// default ones on the options themselves - so the price, the stock badges, the quantity limit
    /// and what goes into the basket all follow. The basket and the checkout price from the same
    /// source. Nothing happens where the answer is empty.
    ///
    /// Once the visitor's country is known: in the browser, and on a server render drawn for a
    /// returning visitor (StoreService.drawnForVisitor), which carries the answer so the browser
    /// applies it at once instead of asking again - the price no longer changes as the page wakes
    /// up. A crawler's render knows no country: it is drawn the default figures, as the structured
    /// data states them, and asks nothing.
    loadMyOffer():void
    {
        if (!this.product || !(this.product.ID > 0)) { return; }

        const code = (this.gate.country() || (this.countryInf && this.countryInf.CountryCode) || '').toUpperCase();
        if (!/^[A-Z]{2}$/.test(code) || this.offerFor === this.product.ID + ':' + code) { return; }
        this.offerFor = this.product.ID + ':' + code;
        this.offerDelivery = {};

        // Only an answer for the very country this browser asks for: the carried one is used as it came.
        const key = ProductComponent.offerKey(this.product.ID, code);
        if (isPlatformBrowser(this.platformId) && this.state.hasKey(key))
        {
            const carried = this.state.get<any[]>(key, null);
            this.state.remove(key);
            this.applyOffers(carried);
            return;
        }

        this.eligibility.productOffer(this.product.ID, code).pipe(takeUntil(this.destroy$)).subscribe(offers =>
        {
            if (isPlatformServer(this.platformId)) { this.state.set(key, offers || []); }
            this.applyOffers(offers);
        }, () => { /* the default figures stand */ });
    }

    private applyOffers(offers:ProductOfferRow[]):void
    {
        if (!offers || offers.length === 0) { return; }

        const byOption:any = {};
        offers.forEach(o => byOption[o.OptionId] = o);

        // Each size's own delivery, where the offer names one: the box reads it for the size on screen.
        const delivery: { [optionId: number]: number } = {};
        offers.forEach(o => { if (o.Delivery !== null && o.Delivery !== undefined) { delivery[o.OptionId] = Number(o.Delivery) || 0; } });
        this.offerDelivery = delivery;

        const apply = (option:any) =>
        {
            const offer = option && byOption[option.ID];
            if (!offer) { return; }
            option.Price = offer.Price;
            option.RegularPrice = offer.RegularPrice;
            option.Discount = offer.Discount;
            option.Quantity = offer.Stock;
        };

        (this.product.OptionList || []).forEach((group:any) => (group.items || []).forEach(apply));
        ((this.product as any).Options || []).forEach(apply);

        // The size on screen may have just sold out for this shopper, or come into stock.
        if (!this.buyableItem(this.selectedPriceOption))
        {
            this.initProductOptions();
        }
        else
        {
            this.selectedMax = this.maxFor(this.selectedPriceOption);
        }

        this.scheduleSizeMeasure();
        this.cd.markForCheck();
    }

    /// Takes a proven person whose country cannot buy this product to the "not available"
    /// page. Never a crawler, an automated browser, the server render or the admin
    /// (CountryGateService), and never from the quick view, which is not a page.
    private leaveIfUnavailable():void
    {
        if (!isPlatformBrowser(this.platformId) || this.layout === 'quickview' || !this.product) { return; }

        // Sent here BY the "not available" page, because the product does sell in the visitor's
        // country after all: sending them straight back would pass them to and fro.
        if (this.sentBackHere()) { return; }

        // On exactly what makes this page say "Currently unavailable" (notDeliverable: the
        // delivery answer once it has loaded, the stored codes until then), so a product the page
        // calls unavailable is never simply shown - opened from inside the app or not.
        if (this.gate.shouldRedirect(this.product) || this.gate.shouldLeave(this.notDeliverable))
        {
            this.router.navigate(['/products/unavailable', this.product.Slug],
                { queryParams: { ref: this.gate.country().toLowerCase() }, replaceUrl: true });
        }
    }

    /// Taken off the storefront because its source ran out of stock (ProductStatus.StockOut). The
    /// page is still served to everyone, everywhere, and says it is out of stock: every variant
    /// reads "Out Of Stock" and the cart is off, whatever a variant's own count says.
    get stockedOut():boolean
    {
        return !!this.product && this.product.ProductStatus === CountryGateService.stockOutStatus;
    }

    /// Taken off the storefront by the owner (ProductStatus.Offline, the inventory's "Take offline", 2026-09-28). Its page
    /// is still served while the product is active, and sells nothing - the checkout sells what is on the storefront only
    /// - so it reads "Currently unavailable", the page's own words for a product that cannot be had, with the wish list and
    /// what is in stock instead. No alert: it is not waiting for stock, and the email is about stock.
    get takenOffline():boolean
    {
        return !!this.product && this.product.ProductStatus === CountryGateService.offlineStatus;
    }

    /// Nothing of this product can be bought right now: taken off the storefront for lack of stock (stockedOut), or
    /// every variant and size at none - by the visitor's own stock once their offer is in. The page then says so once,
    /// where the welcome deal would be, with an email for when it is back and what is in stock instead, and promises no
    /// delivery and no deal. The server render works it out from the same figures, so it says so too - as the JSON-LD
    /// does (OutOfStock), whose rule this is.
    get soldOut():boolean
    {
        if (!this.product) { return false; }
        if (this.stockedOut || this.takenOffline) { return true; }

        const items = this.optionItems();
        if (items.length === 0) { return !(this.product.AvailableQuantity > 0); }
        return !items.some(item => this.buyableItem(item));
    }

    /// The variant or size on screen has none while another has: said where the deal would be, with an email for this one.
    get optionSoldOut():boolean
    {
        return !this.soldOut && !!this.selectedPriceOption && !this.buyableItem(this.selectedPriceOption);
    }

    /// The alert is offered only where the product is sold: a product taken off for lack of stock is shown to everyone,
    /// everywhere, and a visitor whose country it is blocked in is told only that it is out of stock. Null until the
    /// visitor's country is in (and on the server render): the box then holds the form's place rather than showing a form
    /// that may go away. The server decides again (StockAlertService).
    get alertable():boolean | null
    {
        if (this.takenOffline) { return false; }
        const code = ((this.countryInf && this.countryInf.CountryCode) || '').toUpperCase();
        if (!code) { return null; }
        const blocked = (((this.product as any) || {}).BlockedCountryCodes || '').toUpperCase();
        return blocked.indexOf(';' + code + ';') < 0;
    }

    /// The variant and size on screen, as the alert and its email name them ("No Shell · 868MHz"): only what the
    /// shopper had a choice of, and never "Default".
    get optionLabel():string
    {
        const groups = (this.product && this.product.OptionList) || [];
        const sizes = this.shownSizes;
        const named = (text:string) => !!text && !/^default$/i.test(text.trim());
        const parts = [];
        if (groups.length > 1 && named(this.selectedOption && this.selectedOption.option)) { parts.push(this.selectedOption.option.trim()); }
        if (sizes.length > 1 && named(this.selectedPriceOption && this.selectedPriceOption.Size)) { parts.push(this.selectedPriceOption.Size.trim()); }
        return parts.join(' · ');
    }

    /// An "in stock now" card was chosen: reported like any list of products.
    alternativeChosen(product:OnlineProduct, index:number):void
    {
        this.analytics.selectItem(product, AnalyticsService.listFor('Sold out - in stock now'), index);
    }

    /// One variant and size can be bought: switched on and something left - the same test as the page's opening option.
    /// A size that can be bought: switched on, and with stock - or one its seller sells ahead of its stock (a pre-order
    /// before its date, a backorder against what is coming in: product.SellsAhead, worked out by the server on every read).
    private buyableItem(item:any):boolean
    {
        return !!item && item.IsActive !== false && (item.Quantity > 0 || this.aheadOf(item) > 0);
    }

    isBuyable(item:any):boolean
    {
        return this.buyableItem(item);
    }

    /// The delivery window as the page promises it for the size on screen: the usual one, moved on by the seller's days to
    /// dispatch for a pre-order or a backorder. Without those days no window is promised (windowPromised) - the stock is not
    /// there yet to be sent.
    get shownStart(): any { return this.shiftDelivery(this.dStartDate); }
    get shownEnd(): any { return this.shiftDelivery(this.dEndDate); }
    get windowPromised(): boolean { return !this.aheadLabel || this.leadDays() > 0; }

    private leadDays(): number { return Number(this.selectedPriceOption && (this.selectedPriceOption as any).LeadTimeDays) || 0; }

    private shiftDelivery(date: any): any
    {
        if (!date || !this.aheadLabel) { return date; }
        const t = new Date(date).getTime();
        return isNaN(t) ? date : new Date(t + this.leadDays() * 86400000);
    }

    /// How many of a size may be bought beyond its stock right now (0: none).
    aheadOf(item:any):number
    {
        const map = this.product && (this.product as any).SellsAhead;
        return item && map ? (Number(map[item.ID]) || 0) : 0;
    }

    /// The most of a size one basket may take: its stock, or what it sells ahead of it.
    maxFor(item:any):number
    {
        return item ? Math.max(Number(item.Quantity) || 0, this.aheadOf(item)) : 0;
    }

    /// The size on screen has none on the shelf and its seller takes orders for it anyway: what the page says instead of
    /// "In Stock" - a pre-order or a backorder, and when it ships (the seller's days to dispatch, else plainly).
    get aheadLabel(): { kind: string; title: string; note: string } | null
    {
        const item:any = this.selectedPriceOption;
        if (!item || this.soldOut || this.optionSoldOut || item.Quantity > 0 || this.aheadOf(item) <= 0) { return null; }
        const days = Number(item.LeadTimeDays) || 0;
        const when = days === 1 ? this.labels.t('ships-day-after-order', 'Ships about {days} day after you order.', { days })
            : days > 1 ? this.labels.t('ships-days-after-order', 'Ships about {days} days after you order.', { days }) : null;
        if (item.PreorderUntil && new Date(item.PreorderUntil).getTime() > Date.now())
        {
            return { kind: 'preorder', title: this.labels.t('pre-order', 'Pre-order'), note: when || this.labels.t('ships-when-arrives', 'Ships as soon as it arrives.') };
        }
        return { kind: 'backorder', title: this.labels.t('backorder', 'Backorder'), note: when || this.labels.t('ships-next-delivery', 'Ships when the next delivery arrives.') };
    }

    /// The sizes of the colour on screen as a shopper reads them: rows whose sizes read the same - spacing and case
    /// aside, and "Default" the same as no name - are ONE size, shown by the row that can be bought (else the first).
    /// A seller who replaced a SKU used to leave the colour a sold-out "Default" and blank sizes beside it. The worker
    /// now keeps one row per size (VariantAxes.ReadsTheSame, the same rule); this keeps the page right until it has
    /// been round, and for a row an order still holds.
    get shownSizes():any[]
    {
        const shown:any[] = [];
        const at = new Map<string, number>();
        ((this.selectedOption && this.selectedOption.items) || []).forEach((item:any) =>
        {
            if (!item) { return; }
            const key = ProductComponent.sizeAsRead(item.Size);
            if (!at.has(key)) { at.set(key, shown.length); shown.push(item); }
            else if (!this.buyableItem(shown[at.get(key)]) && this.buyableItem(item)) { shown[at.get(key)] = item; }
        });
        return shown;
    }

    /// A size to choose: more than one, or one with a name - never a lone blank or "Default".
    get hasSizeChoice():boolean
    {
        const sizes = this.shownSizes;
        return sizes.length > 1 || (sizes.length === 1 && ProductComponent.sizeAsRead(sizes[0].Size) !== '');
    }

    private static sizeAsRead(size:string):string
    {
        const text = (size || '').trim().replace(/\s+/g, ' ').toLowerCase();
        return text === 'default' ? '' : text;
    }

    /// A colour with at least one size that can be bought. It used to look at the colour's first size only.
    groupBuyable(group:any):boolean
    {
        return ((group && group.items) || []).some(item => this.buyableItem(item));
    }

    /// Every variant's every size, flat.
    private optionItems():any[]
    {
        const items = [];
        ((this.product && this.product.OptionList) || []).forEach((group:any) => ((group && group.items) || []).forEach(item => item && items.push(item)));
        return items.length > 0 ? items : ((this.product && (this.product as any).Options) || []);
    }

    /// The "not available" page sends a visitor on to the product, with skipCountryCheck in the
    /// navigation's state, when the product sells in their country after all.
    private sentBackHere():boolean
    {
        try
        {
            const state:any = isPlatformBrowser(this.platformId) ? window.history.state : null;
            return !!(state && state.skipCountryCheck);
        }
        catch { return false; }
    }

    /// The signed-in admin, who alone is told WHY a product is unavailable - except while they
    /// see the shop as a visitor from another country, when they see what that visitor sees.
    isAdminViewer():boolean
    {
        return this.user.isNijera() && !this.gate.previewing();
    }

    /// True when the shop knows this product cannot be delivered to the visitor's country. The
    /// shipping box then says so instead of promising free shipping, and the cart button is
    /// off - for everyone who reaches the page, the admin included. The stored delivery facts
    /// answer it once they have loaded (they include the owner's overrides); until then the
    /// product's own BlockedCountryCodes does, so the button is never briefly live. Only once the
    /// visitor's country is known - in the browser, or on a render drawn for a returning visitor; a
    /// crawler's render knows no country and is never told this.
    get notDeliverable():boolean
    {
        // Out of stock at the source, or taken offline: the page says that, the same everywhere - not where it delivers.
        if (this.stockedOut || this.takenOffline) { return false; }

        const code = ((this.countryInf && this.countryInf.CountryCode) || '').toUpperCase();
        if (!code) { return false; }
        // "unknown" counts as not deliverable: not known to deliver is not offered.
        if (this.myShipping) { return this.myShipping.State !== 'ships'; }
        // A seller's product has a row for every country the seller may serve; none for the visitor's
        // means the seller does not deliver there - there is no default rate to fall back on.
        if (this.shipMap && (this.shipMap as any).SellerDelivery) { return true; }

        const blocked = (((this.product as any) || {}).BlockedCountryCodes || '').toUpperCase();
        return blocked.indexOf(';' + code + ';') >= 0;
    }

    /// The parcel stays inside the shopper's own customs area (the EU single market, or their
    /// country itself - the map's Local): it arrives sooner, and there is no customs or import
    /// charge at the door. Worth saying, and true by that definition. The visitor's own row only,
    /// so browser only, and never on a product that cannot reach them.
    get shipsLocal():boolean
    {
        const row = this.myCountryRow();
        return !!row && row.State === 'ships' && row.Local === true && !this.notDeliverable;
    }

    /// The same promise as the dates above it, in days: handling plus the supplier's window.
    deliveryDays(row:CountryShipping):string
    {
        if (!row || !(row.MaxDays > 0)) { return ''; }
        const [handlingMin, handlingMax] = this.handlingDays();
        const max = row.MaxDays + handlingMax;
        const min = (row.MinDays > 0 && row.MinDays < row.MaxDays ? row.MinDays : row.MaxDays) + handlingMin;
        return min < max ? this.labels.t('days-range', '{min}–{max} days', { min: min, max: max }) : this.labels.t('days-count', '{count} days', { count: max });
    }

    /// The delivery dates follow the visitor's country once it is known (pickMyShipping) - a
    /// crawler's render keeps the default country's. The table's row is the warehouse that serves
    /// the visitor, so it wins over a live quote.
    private applyCountryDelivery():void
    {
        const row = this.myCountryRow();
        if (row && row.State === 'ships' && row.MaxDays > 0)
        {
            this.shipping = null;
            this.setDeliveryDates(row.MinDays, row.MaxDays);
        }
    }

    // ---------------------------------------------------------------- owner: the country matrix

    // ---------------------------------------------------------------- owner: the product's history

    toggleHistory():void
    {
        this.historyOpen = !this.historyOpen;
        if (this.historyOpen && !this.history) { this.loadHistory(); }
    }

    /// older = the next page after the last line shown; otherwise the newest again.
    loadHistory(older:boolean = false):void
    {
        if (!this.product || !this.product.ID || this.historyLoading) { return; }
        const before = older && this.history && this.history.length ? this.history[this.history.length - 1].ID : null;
        this.historyLoading = true;
        this.nijer.getProductHistory(this.product.ID, before).pipe(takeUntil(this.destroy$)).subscribe(res =>
        {
            const entries = (res && res.Entries) || [];
            this.history = older && this.history ? this.history.concat(entries) : entries;
            this.historyMore = !!(res && res.More);
            this.historyRows = this.groupHistory(this.history);
            this.historyLoading = false;
            this.cd.markForCheck();
        }, () =>
        {
            this.historyLoading = false;
            this.toastr.error(this.labels.t('product-owner-history-failed', 'Could not load the history.'));
            this.cd.markForCheck();
        });
    }

    /// Checks in a row by the same reader that changed nothing read as one line: "Checked 12 times".
    groupHistory(entries:any[]):any[]
    {
        const rows:any[] = [];
        for (const e of entries)
        {
            const quiet = e.Action === 'Checked' && !e.HasChanges;
            const last = rows.length ? rows[rows.length - 1] : null;
            if (quiet && last && last.quiet && last.entry.Actor === e.Actor && last.entry.Summary === e.Summary)
            {
                last.count++;
                last.first = e.At;
                continue;
            }
            rows.push({ entry: e, quiet: quiet, count: 1, first: e.At });
        }
        return rows;
    }

    historyWho(e:any):string
    {
        switch (e.Actor)
        {
            case 'Crawler': return this.labels.t('product-owner-crawler', 'Crawler');
            case 'Api': return 'API';
            case 'Human': return e.ActorName || this.labels.t('product-owner-signed-in-user', 'Signed-in user');
            default: return e.ActorName || this.labels.t('product-owner-system', 'System');
        }
    }

    historyWhat(e:any):string
    {
        switch (e.Action)
        {
            case 'CheckFailed': return this.labels.t('product-owner-check-failed', 'Check failed');
            case 'Created': return e.Actor === 'Crawler' || e.Actor === 'Api' ? this.labels.t('product-owner-imported', 'Imported') : this.labels.t('product-owner-created', 'Created');
            default: return e.Action;
        }
    }

    toggleMatrix():void
    {
        this.matrixOpen = !this.matrixOpen;
        if (this.matrixOpen && !this.matrix) { this.loadMatrix(); }
    }

    loadMatrix():void
    {
        if (!this.product || !this.product.ID || this.matrixLoading) { return; }
        this.matrixLoading = true;
        this.eligibility.matrix(this.product.ID).pipe(takeUntil(this.destroy$)).subscribe(res =>
        {
            this.matrix = res;
            this.matrixLoading = false;
            this.cd.markForCheck();
        }, () =>
        {
            this.matrixLoading = false;
            this.toastr.error(this.labels.t('product-owner-matrix-failed', 'Could not load the country matrix.'));
            this.cd.markForCheck();
        });
    }

    /// A row's state for the owner. "Unknown" from the server covers three different things,
    /// and they must not read the same: nobody has asked yet; the supplier was asked once and
    /// offered nothing, which is not believed until a second answer hours later agrees; or the
    /// call itself failed and proves nothing.
    matrixKind(r:any):string
    {
        if (!r) { return 'Unknown'; }
        if (r.Status !== 'Unknown') { return r.Status; }
        if (r.Shipping && r.Shipping.EmptyAnswers > 0) { return 'Confirming'; }
        if (r.Shipping && r.Shipping.FailCount > 0) { return 'Failed'; }
        return 'Unknown';
    }

    matrixCount(kind:string):number
    {
        return this.matrix && this.matrix.Rows ? this.matrix.Rows.filter((r:any) => this.matrixKind(r) === kind).length : 0;
    }

    matrixStateClass(r:any):string
    {
        switch (this.matrixKind(r))
        {
            case 'Eligible': return 'owner-panel__state--ok';
            case 'EligibleWithWarnings': return 'owner-panel__state--warn';
            case 'Blocked': return 'owner-panel__state--bad';
            case 'CountryNotLive': return 'owner-panel__state--dark';
            case 'Confirming': return 'owner-panel__state--warn';
            default: return 'owner-panel__state--muted';
        }
    }

    matrixStateLabel(r:any):string
    {
        switch (this.matrixKind(r))
        {
            case 'Eligible': return this.labels.t('product-owner-state-sells', 'Sells');
            case 'EligibleWithWarnings': return this.labels.t('product-owner-state-sells-warnings', 'Sells, warnings');
            case 'Blocked': return this.labels.t('product-owner-state-blocked', 'Blocked');
            case 'CountryNotLive': return this.labels.t('product-owner-state-country-off', 'Country off');
            case 'Confirming': return this.labels.t('product-owner-state-confirming', 'No delivery found · confirming');
            case 'Failed': return this.labels.t('product-owner-state-retrying', 'Check failed · retrying');
            default: return this.labels.t('product-owner-state-not-checked', 'Not checked yet');
        }
    }

    matrixHelp(kind:string):string
    {
        switch (kind)
        {
            case 'Confirming': return this.labels.t('product-owner-help-confirming', 'The supplier was asked and offered no delivery option. One empty answer is not believed - it is as often a blip - so the question is asked again at least 12 hours later. Empty again: Blocked. An option: Sells. Until then the product is treated as available.');
            case 'Failed': return this.labels.t('product-owner-help-failed', 'The call did not get an answer (quota, token or network). It proves nothing about the country and is retried.');
            case 'Unknown': return this.labels.t('product-owner-help-unknown', 'Nobody has asked the supplier about this country yet. The sweep works through priority countries first, within its daily budget.');
            case 'Blocked': return this.labels.t('product-owner-help-blocked', 'The supplier offered no delivery option twice running, or you blocked it. Visitors from here are sent to the "not available" page.');
            default: return '';
        }
    }

    /// Allow or block this product in one country. The reason is asked for because it is kept
    /// with the override - the server refuses one without it.
    async overrideCountry(row:any, decision:number):Promise<void>
    {
        if (!isPlatformBrowser(this.platformId)) { return; }

        const reason = (await askPrompt(decision === 1
            ? this.labels.t('product-owner-allow-prompt', 'ALLOW this product in {country}. Why? (kept with the override)', { country: row.Name })
            : this.labels.t('product-owner-block-prompt', 'BLOCK this product in {country}. Why? (kept with the override)', { country: row.Name })));
        if (!reason || !reason.trim()) { return; }

        this.eligibility.addOverride({ ProductId: this.product.ID, CountryCode: row.CountryCode, Decision: decision, Reason: reason.trim() })
            .pipe(takeUntil(this.destroy$)).subscribe(res =>
            {
                if (res && res.success) { this.toastr.success(res.message); this.loadMatrix(); }
                else { this.toastr.error((res && res.message) || this.labels.t('product-owner-override-save-failed', 'Could not save the override.')); }
            }, () => this.toastr.error(this.labels.t('product-owner-override-save-failed', 'Could not save the override.')));
    }

    retireOverride(id:number):void
    {
        this.eligibility.retireOverride(id).pipe(takeUntil(this.destroy$)).subscribe(res =>
        {
            if (res && res.success) { this.toastr.success(res.message); this.loadMatrix(); }
        }, () => this.toastr.error(this.labels.t('product-owner-override-retire-failed', 'Could not retire the override.')));
    }

    recheckCountries():void
    {
        this.eligibility.recheck(this.product.ID).pipe(takeUntil(this.destroy$)).subscribe(res =>
        {
            if (res && res.success) { this.toastr.success(res.message); this.loadMatrix(); }
        }, () => this.toastr.error(this.labels.t('product-owner-recheck-failed', 'Could not queue the recheck.')));
    }

    /// Shown unless the owner hid it in this browser. Storage can be missing or refuse (a
    /// private window), and then the view simply shows.
    private ownerPanelPreference(): boolean
    {
        if (!isPlatformBrowser(this.platformId)) { return true; }
        try { return localStorage.getItem(ProductComponent.OwnerPanelKey) !== 'hidden'; }
        catch { return true; }
    }

    /// The eye beside Edit and Delete: shows or hides the whole owner view, and remembers it.
    toggleOwnerPanel(): void
    {
        this.ownerPanelOpen = !this.ownerPanelOpen;
        try { localStorage.setItem(ProductComponent.OwnerPanelKey, this.ownerPanelOpen ? 'shown' : 'hidden'); }
        catch { /* the choice lasts for this page only */ }
        if (this.ownerPanelOpen) { this.loadOwnerInfo(); }
        this.cd.markForCheck();
    }

    loadOwnerInfo(force:boolean = false)
    {
        if (!isPlatformBrowser(this.platformId) || !this.product || !this.product.ID) { return; }
        if (this.ownerInfoLoading || (this.ownerInfo && !force)) { return; }
        this.ownerInfoLoading = true;
        this.ownerInfoError = null;
        this.nijer.getSourceForProduct(this.product.ID).pipe(takeUntil(this.destroy$)).subscribe(res =>
        {
            this.ownerInfo = res || null;
            this.ownerInfoLoading = false;
            this.cd.markForCheck();
        }, () =>
        {
            this.ownerInfoError = this.labels.t('product-owner-info-failed', 'Could not load the inventory details.');
            this.ownerInfoLoading = false;
            this.cd.markForCheck();
        });
    }

    /// Our price against what the source last charged for the cheapest variant.
    ownerMarginPercent(): number
    {
        const ours = Number(this.ownerInfo && this.ownerInfo.Product && this.ownerInfo.Product.Price) || 0;
        const cost = Number(this.ownerInfo && this.ownerInfo.Source && this.ownerInfo.Source.LastPrice) || 0;
        return ours > 0 && cost > 0 ? (ours - cost) / ours * 100 : 0;
    }

    ownerMarginClass(): string
    {
        const m = this.ownerMarginPercent();
        return m <= 0 ? 'text-danger' : (m < 15 ? 'text-warning' : 'text-success');
    }

    productStatusClass(status:number): string
    {
        switch (status)
        {
            // The panel's own pills, not Bootstrap's .badge: this page's stylesheet paints every
            // .badge amber for the commitment strip, which made Published read as a warning.
            case 3: return 'owner-panel__state--ok';
            case 6: return 'owner-panel__state--dark';
            case 7: return 'owner-panel__state--dark';
            case 1: return 'owner-panel__state--warn';
            case 5: return 'owner-panel__state--bad';
            default: return 'owner-panel__state--muted';
        }
    }

    sourceStatusClass(status:number): string
    {
        switch (status)
        {
            case 1: return 'owner-panel__state--muted';
            case 2: return 'owner-panel__state--ok';
            case 3: return 'owner-panel__state--warn';
            case 4: return 'owner-panel__state--bad';
            default: return 'owner-panel__state--muted';
        }
    }

    /// "12m ago", "in 6h", "3d ago" - the page is read at a glance.
    relative(value:any): string
    {
        if (!value) { return '—'; }
        const t = new Date(value).getTime();
        if (isNaN(t)) { return '—'; }
        const diff = Date.now() - t;
        const abs = Math.abs(diff);
        const m = Math.round(abs / 60000);
        const h = Math.round(abs / 3600000);
        const d = Math.round(abs / 86400000);
        if (m < 1) { return this.labels.t('just-now', 'just now'); }
        const span = m < 60 ? this.labels.t('minutes-short', '{count}m', { count: m })
            : (h < 48 ? this.labels.t('hours-short', '{count}h', { count: h }) : this.labels.t('days-short', '{count}d', { count: d }));
        return diff >= 0 ? this.labels.t('time-ago', '{time} ago', { time: span }) : this.labels.t('time-in', 'in {time}', { time: span });
    }

    async deleteProd(){
        if(this.deleting || !isPlatformBrowser(this.platformId)){
            return;
        }
        // Always asked, every time: deleting takes the variants, reviews, images and the
        // tracked listing with it, and there is no undo.
        const name = (this.product && (this.product.ShortName || this.product.Name)) || this.labels.t('product-this-product', 'this product');
        if (!(await askConfirm(this.labels.t('product-owner-delete-confirm', 'Delete "{name}" for good?', { name: name }) + '\n\n'
            + this.labels.t('product-owner-delete-confirm-detail', 'This removes its variants, reviews, pictures and its tracked listing. It cannot be undone.')))) {
            return;
        }
        this.deleteConfirmed(null);
    }

    /// A product that has been sold comes back refused (NeedsConfirmation) with what it has sold
    /// and what deleting would do: warned a second time, the owner types its SKU to go on - the
    /// server checks the typed text itself. Null when they stop at either step.
    private async confirmSoldDelete(res:any): Promise<string>
    {
        if (!(await askConfirm(this.labels.t('product-owner-has-been-sold', 'This product has been sold.') + '\n\n' + res.Message + '\n\n'
            + this.labels.t('continue-question', 'Continue?')))) { return null; }
        const typed = (await askPrompt(this.labels.t('product-owner-type-to-delete', 'Last step. Type {text} to delete it for good:', { text: res.ConfirmWith }), ''));
        if (typed === null) { return null; }
        if (typed.trim().toLowerCase() !== String(res.ConfirmWith).toLowerCase())
        {
            (await showAlert(this.labels.t('product-owner-not-match', 'That is not {text}, so nothing was deleted.', { text: res.ConfirmWith })));
            return null;
        }
        return typed.trim();
    }

    private deleteConfirmed(confirm:string): void
    {
        this.deleting = true;
        this.shop.deleteProd(this.product.PUID, this.product.SID, confirm).pipe(takeUntil(this.destroy$)).subscribe(res =>
        {
            if(res && res.NeedsConfirmation && !confirm)
            {
                this.deleting = false;
                this.confirmSoldDelete(res).then(typed => { if(typed) { this.deleteConfirmed(typed); } });
                return;
            }
            // The endpoint answers with { Success, Message, MediaRemoved } now, not a bare
            // boolean. A refusal - a product that has been ordered - comes back as Success
            // false with the reason, which is worth showing instead of "try again".
            if(res && res.Success === true){
                this.router.navigate(['/']);
            }
            else{
                this.toastr.error((res && res.Message) || this.labels.t('product-owner-delete-failed', 'Could not delete, try again!'));
            }
            this.deleting = false;
        }, error=>{
            this.deleting = false;
            this.toastr.error(error && error.status === 403
                ? this.labels.t('product-owner-delete-forbidden', 'Only the site owner can delete products.')
                : this.labels.t('product-owner-delete-failed', 'Could not delete, try again!'));
        })
    }
}
